import React, { useEffect, useRef, useState } from "react";
import Gantt from "frappe-gantt";

import "./DashboardGantt.css";
import "../../styles/frappe-gantt.css";
import { headers } from "../utils/utils";

const DashboardGantt = ({ board, startDate, endDate }) => {
  const ganttRef = useRef(null);
  const ganttInstance = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!board || !startDate || !endDate) return;

    fetchGanttData();

    // Cleanup on unmount
    return () => {
      if (ganttInstance.current) {
        ganttInstance.current = null;
      }
    };
  }, [board, startDate, endDate]);

  const fetchGanttData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/gantt?board=${board}&startDate=${startDate}&endDate=${endDate}`,
        {
          method: "GET",
          headers: headers(),
          credentials: "include",
        }
      );

      const data = await res.json();
      console.log(data)
      const tasks = mapBackendToGantt(data);

      if (!tasks.length) {
        if (ganttRef.current) ganttRef.current.innerHTML = "";
        ganttInstance.current = null;
        setLoading(false);
        return;
      }

      requestAnimationFrame(() => {
        try {
          if (ganttInstance.current) {
            // refresh() can also trigger the "classList" error if 
            // the new tasks exclude "Today"
            ganttInstance.current.refresh(tasks);
          } else if (ganttRef.current) {
            ganttInstance.current = new Gantt(ganttRef.current, tasks, {
              view_mode: tasks.length > 30 ? "Week" : "Day",
              date_format: "YYYY-MM-DD",
              custom_popup_html: (task) => {
                if (task.id === "ghost-today") return "";
                const membersHtml = task.members?.length
                ? task.members.map(m => `<li>${m}</li>`).join("")
                : "<li>Unassigned</li>";
                return `
                  <div class="details-container">
                    <h5>${task.name}</h5>
                    <p>Status: <b>${task.status}</b></p>
                    <p>Start: ${task.start}</p>
                    <p>End: ${task.end}</p>
                    <div style="margin-top:8px">
        <b>Members:</b>
        <ul style="margin:4px 0 0 16px;padding:0">  
          ${membersHtml}
        </ul>
      </div>
                  </div>
                `;
              },
            });
            ganttInstance.current.scroll_current = () => {};
          }
            // 2️⃣ NEXT FRAME → sync horizontal scroll
            requestAnimationFrame(() => {
              const container = ganttRef.current;
              const topScroll = topScrollRef.current;
              const svg = container?.querySelector("svg");
              const inner = topScroll?.querySelector(".gantt-x-scroll-inner");
              const header = container?.querySelector(".grid-header");
            
              if (!container || !svg || !topScroll || !inner) return;
            
              // Sync width for top scrollbar
              const svgWidth = svg.getBBox().width;
              inner.style.width = `${svgWidth}px`;
            
              container.onscroll = () => {
                // 1. Sync top scrollbar position
                topScroll.scrollLeft = container.scrollLeft;
            
                // 2. STICKY HEADER: Manually move the SVG header group down
                if (header) {
                  header.setAttribute("transform", `translate(0, ${container.scrollTop})`);
                }
              };
            
              topScroll.onscroll = () => {
                container.scrollLeft = topScroll.scrollLeft;
              };
            });
        } catch (err) {
          // This catches the frappe-gantt internal scroll error 
          // while still allowing the chart to render.
          console.warn("Gantt auto-scroll failed, but chart should still be visible.", err);
        }
      });
    } catch (err) {
      console.error("Failed to load gantt", err);
    } finally {
      setLoading(false);
    }
  };
  const topScrollRef = useRef(null);
  return (

    <div className="gantt-wrapper">
      <div ref={topScrollRef} className="gantt-x-scroll">
      <div className="gantt-x-scroll-inner" />
      </div>

      {loading && <div className="gantt-loading">Loading Gantt...</div>}

        <div ref={ganttRef} className="gantt-container" />
    </div>

  );
};

export default DashboardGantt;

/* -------------------- Helpers -------------------- */

function mapBackendToGantt(data) {
  const tasks = data
    .filter((item) => item.start && item.end)
    .map((item) => ({
      id: String(item.id),
      name: item.name,
      start: toYMD(item.start),
      end: toYMD(item.end),
      dependencies: item.project || "",
      progress: item.status === "COMPLETED" ? 100 : 0,
      status: item.status,
      members: item.members || [], 
      custom_class: statusToClass(item.status),
    }));

  // Force "Today" into the timeline to prevent the "classList" of undefined error
  if (tasks.length > 0) {
    const today = new Date().toISOString().substring(0, 10);
    tasks.push({
      id: "ghost-today",
      name: "",
      start: today,
      end: today,
      progress: 0,
      custom_class: "gantt-ghost-task",
    });
  }

  return tasks;
}

function toYMD(dateStr) {
  if (!dateStr) return null;
  return dateStr.substring(0, 10);
}

function statusToClass(status) {
  switch (status) {
    case "COMPLETED": return "gantt-completed";
    case "OVERDUE":   return "gantt-overdue";
    default:          return "gantt-inprogress";
  }
}